import React, { useEffect, useState } from 'react'
import {
    useBreakpointValue
} from '@salesforce/retail-react-app/app/components/shared/ui'

/* Map components */
import { Map, Marker, useMap } from '@vis.gl/react-google-maps';

/* Configs */
import { STORE_LOCATOR_CONFIGS } from '../configs'
import { MAP_THEMES } from '../map-themes'

/* Context */
import { useStoreLocator } from '../../../contexts/store-locator-context';

const CustomMarker = ({ position, id, isActive, onClick, number, store }) => {
    const createNumberedIcon = (isActive, number) => {
        const svgIcon = `
            <svg width="24" height="29" viewBox="0 0 24 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="24" height="24.96" rx="4.25978" fill="black"/>
                <path d="M12.4813 28.7998L9.98718 24.4798L14.9755 24.4798L12.4813 28.7998Z" fill="black"/>
                <text
                    x="12" y="18"
                    font-family="sans-serif"
                    font-weight="500"
                    font-size="15px"
                    text-anchor="middle"
                    fill="#ffffff">${number}</text>
            </svg>
        `;

        const activeSvgIcon = `
            <svg width="34" height="41" viewBox="0 0 34 41" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="34.0004" height="35.36" rx="6.03473" fill="#EC008C"/>
            <path d="M17.6817 40.7993L14.1482 34.6793L21.2151 34.6793L17.6817 40.7993Z" fill="#EC008C"/>
            <text
                x="17" y="25"
                font-family="sans-serif"
                font-weight="500"
                font-size="24px"
                text-anchor="middle"
                fill="#ffffff">${number}</text>
            </svg>
        `;

        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(isActive ? activeSvgIcon : svgIcon)}`
        };
    };

    const handleMarkerClick = () => {
        if (onClick) {
            onClick(store, id);
        }
    };

    return (
        <Marker
            position={position}
            icon={createNumberedIcon(isActive, number)}
            onClick={handleMarkerClick}
        />
    );
}

const StoreLocatorMap = ({
    openGoogleMapsOnClickMarker = false
}) => {
    const isMobile = useBreakpointValue({ base: true, md: false })

    const {
        setMap,
        stores,
        selectedStore,
        setSelectedStore,
        activeMarkerId,
        setActiveMarkerId,
        setToggleStoreInfo
    } = useStoreLocator()
    const map = useMap()
    const [defaultCenter, setDefaultCenter] = useState({
        lat: STORE_LOCATOR_CONFIGS.defaultCenter.latitude,
        lng: STORE_LOCATOR_CONFIGS.defaultCenter.longitude
    })

    const fitMapBounds = (stores) => {
        if (!map || !window.google?.maps || stores?.length === 0) return;

        const bounds = new google.maps.LatLngBounds();
        stores?.forEach(store => {
            bounds.extend(new google.maps.LatLng(store.latitude, store.longitude));
        });

        map?.fitBounds(bounds);
    }

    useEffect(() => {
        fitMapBounds(stores)
        setMap(map)
    }, [map, stores]);

    useEffect(() => {
        // Center and zoom selected store on the map
        if (selectedStore && map) {
            map.panTo({ lat: selectedStore.latitude, lng: selectedStore.longitude })
            map.setZoom(STORE_LOCATOR_CONFIGS.centerZoom)
        }
    }, [selectedStore]);


    const handleMarkerClick = (store, markerId) => {
        setActiveMarkerId(markerId)
        setSelectedStore(store)

        if (isMobile) {
            setToggleStoreInfo(true)
        }

        if (openGoogleMapsOnClickMarker && store && store?.latitude && store?.longitude) {
            window.open(STORE_LOCATOR_CONFIGS.getDirectionURL + store?.latitude + ',' + store?.longitude, '_blank')
        }
    };

    // Get the selected theme
    const selectedTheme = MAP_THEMES[STORE_LOCATOR_CONFIGS.mapTheme] || MAP_THEMES.default

    return (
        <Map
            style={{
                width: '100%',
                height: '100%',
                ...(isMobile ? { width: '100vw', height: '515px' } : {}) // Apply different height for mobile
            }}
            defaultCenter={defaultCenter}
            defaultZoom={STORE_LOCATOR_CONFIGS.initialZoom}
            gestureHandling={STORE_LOCATOR_CONFIGS.gestureHandling}
            disableDefaultUI={STORE_LOCATOR_CONFIGS.disableDefaultUI}
            styles={selectedTheme}>
            {stores?.map((store, index) => (
                <CustomMarker
                    key={`marker-${store?.id}`}
                    id={store?.id}
                    position={{ lat: store.latitude, lng: store.longitude }}
                    number={index + 1}
                    isActive={activeMarkerId === store?.id}
                    onClick={handleMarkerClick}
                    store={store} />
            ))}
        </Map>
    )
}

export default StoreLocatorMap
